import { StorageError } from './StorageProvider';

/**
 * A very small promise wrapper over raw IndexedDB.
 *
 * We deliberately avoid a wrapper library here: the DAW uses perhaps a dozen
 * IDB calls in total, and owning them means we can map failures onto the
 * DAW's own error vocabulary (quota, blocked, corrupt) and give the musician a
 * message that says what to do next.
 */

export function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

export function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });
}

export function isQuotaError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

export function toStorageError(error: unknown, action: string): StorageError {
  if (error instanceof StorageError) return error;
  if (isQuotaError(error)) {
    return new StorageError(
      'quota',
      `There is not enough browser storage left to ${action}. Free space by deleting projects or unused audio, ` +
        'then try again. Your current work is still in memory.',
      error,
    );
  }
  if (error instanceof DOMException && error.name === 'InvalidStateError') {
    return new StorageError(
      'unavailable',
      'Browser storage is unavailable. Private-browsing windows and blocked site data both cause this; ' +
        'Stage Zero can still run, but projects cannot be saved.',
      error,
    );
  }
  return new StorageError('unknown', `Could not ${action}: ${describe(error)}`, error);
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export interface OpenDatabaseOptions {
  name: string;
  version: number;
  upgrade: (db: IDBDatabase, oldVersion: number, transaction: IDBTransaction) => void;
  /** Called when another tab holds an older connection open. */
  onBlocked?: () => void;
}

export async function openDatabase(options: OpenDatabaseOptions): Promise<IDBDatabase> {
  const idb = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  if (!idb) {
    throw new StorageError(
      'unavailable',
      'This browser does not expose IndexedDB, so projects cannot be stored locally. ' +
        'Stage Zero still runs, but save and autosave are disabled.',
    );
  }

  return new Promise<IDBDatabase>((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = idb.open(options.name, options.version);
    } catch (error) {
      reject(toStorageError(error, 'open local storage'));
      return;
    }

    request.onupgradeneeded = (event) => {
      const db = request.result;
      const tx = request.transaction;
      if (!tx) return;
      try {
        options.upgrade(db, event.oldVersion, tx);
      } catch (error) {
        tx.abort();
        reject(toStorageError(error, 'upgrade local storage'));
      }
    };

    request.onblocked = () => {
      options.onBlocked?.();
      reject(
        new StorageError(
          'blocked',
          'Another Stage Zero tab is using an older version of the local database. ' +
            'Close other Stage Zero tabs and reload this one.',
        ),
      );
    };

    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };

    request.onerror = () => reject(toStorageError(request.error, 'open local storage'));
  });
}
